import React from "react";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

interface AttachmentFileCardProps {
  fileName: string;
  caption?: string | null;
  isDownloading: boolean;
  /** Opens the document in a new tab - the file name's own control. */
  onView: () => void;
  /** Saves the document to disk - the trailing icon's own control. */
  onDownload: () => void;
  testId: string;
}

/**
 * DBCO-0030 - a copy of Bay Hire's `BayHireFileCard` (kept inside this feature, no cross-feature import): the file
 * name and the download icon are two independent controls.
 */
const AttachmentFileCard: React.FC<AttachmentFileCardProps> = ({
  fileName,
  caption,
  isDownloading,
  onView,
  onDownload,
  testId,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-4 py-3 bg-white"
      data-testid={`${testId}-card`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <InsertDriveFileOutlinedIcon fontSize="small" className="text-gray-400 shrink-0" />
        <div className="min-w-0">
          <button
            type="button"
            onClick={onView}
            disabled={isDownloading}
            data-testid={testId}
            className="text-sm text-blue-600 hover:underline truncate disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
          >
            {fileName}
          </button>
          {caption ? <p className="text-xs text-gray-500 truncate">{caption}</p> : null}
        </div>
      </div>
      {isDownloading ? (
        <CircularProgress size={16} data-testid={`${testId}-spinner`} />
      ) : (
        <button
          type="button"
          onClick={onDownload}
          data-testid={`${testId}-download`}
          aria-label={t("common.download", { defaultValue: "Download" })}
          className="text-gray-400 hover:text-gray-600 shrink-0"
        >
          <DownloadOutlinedIcon fontSize="small" />
        </button>
      )}
    </div>
  );
};

export default AttachmentFileCard;
